import { useState } from "react";
import { Check, Plus, Send, MessageCircle, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { ADD_ONS, WHATSAPP_NUMBER } from "@/lib/constants";
import { AI_COURSE } from "@/components/landing/AiCourseLeadForm";
import { PayPalCheckout } from "@/components/landing/PayPalCheckout";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function AddOnCard({ name, on, toggle }) {
  return (
    <button type="button" onClick={toggle} aria-pressed={on} data-testid={`quote-addon-${slug(name)}`}
      className={`text-left rounded-2xl border p-5 transition-colors ${on ? "bg-[#0B2A4A] text-[#F5ECD7] border-[#0B2A4A]" : "bg-[#FAF6EE] border-dashed border-[#1A9AA8]/50 hover:border-[#1A9AA8]"}`}>
      {on ? <Check className="h-5 w-5 text-[#F08A3C]" /> : <Plus className="h-5 w-5 text-[#F08A3C]" />}
      <h3 className={`mt-3 font-semibold ${on ? "text-white" : "text-[#0B2A4A]"}`}>{name}</h3>
      <p className={`mt-1 text-xs font-mono-ui uppercase tracking-wider ${on ? "text-[#F5ECD7]/70" : "text-slate-500"}`}>Priced by scope · custom quote</p>
    </button>
  );
}

export function summaryText(picked, other) {
  const lines = [`My custom program — ${AI_COURSE}`, "• Core course: starting at $5,000"];
  picked.forEach((a) => lines.push(`• Add-on: ${a} (custom quote)`));
  if (other.trim()) lines.push(`• Also interested in: ${other.trim()}`);
  return lines.join("\n");
}

export function QuoteBuilder() {
  const [picked, setPicked] = useState([]);
  const [other, setOther] = useState("");
  const [c, setC] = useState({ name: "", email: "", whatsapp: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const toggle = (a) => setPicked((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));
  const text = summaryText(picked, other);
  const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Greg! ${text}`)}`;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...c, journey_stage: "Custom program quote", goals: text, add_ons: picked,
        add_ons_other: other.trim(), interest: AI_COURSE, contact_pref: "Email" });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-8" data-testid="quote-builder">
      <div className="flex flex-col gap-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ADD_ONS.map((a) => <AddOnCard key={a} name={a} on={picked.includes(a)} toggle={() => toggle(a)} />)}
        </div>
        <Input value={other} onChange={(e) => setOther(e.target.value)} maxLength={300} placeholder="And more — tell us anything else you'd like included"
          aria-label="Something else" data-testid="quote-other-input" className="bg-white" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
      <aside className="rounded-3xl bg-white border border-[#0B2A4A]/10 shadow-xl shadow-[#0B2A4A]/10 p-6 sm:p-8" data-testid="quote-summary">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">OPTION 1 · NOT SURE YET?</p>
        <h3 className="mt-2 text-xl font-bold text-[#0B2A4A]">Get a custom quote</h3>
        <p className="mt-1 text-sm text-slate-600">Send your program to Mr. Greg. No payment needed — he replies with a personal quote.</p>
        <ul className="mt-4 flex flex-col gap-2 text-sm">
          <li className="flex justify-between gap-3 font-semibold text-[#0B2A4A]"><span>{AI_COURSE}</span><span className="text-[#F08A3C] whitespace-nowrap" data-testid="quote-base-price">from $5,000</span></li>
          {picked.map((a) => <li key={a} className="flex justify-between gap-3 text-slate-700" data-testid={`quote-summary-${slug(a)}`}><span>+ {a}</span><span className="text-slate-500 whitespace-nowrap">custom quote</span></li>)}
          {other.trim() && <li className="text-slate-700" data-testid="quote-summary-other">+ {other.trim()}</li>}
        </ul>
        <p className="mt-4 pt-4 border-t border-dashed border-[#0B2A4A]/15 text-xs text-slate-500">Final price depends on the extent of the teachings and add-ons. Mr. Greg will reply with a personal quote.</p>
        {sent ? (
          <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-[#147C88]" data-testid="quote-sent-success"><CheckCircle2 className="h-5 w-5" /> Sent! Mr. Greg will reply with your quote.</p>
        ) : (
          <form onSubmit={send} className="mt-5 flex flex-col gap-3" data-testid="quote-form">
            <Input required placeholder="Your name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="quote-name-input" />
            <Input required type="email" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="quote-email-input" />
            <Input required type="tel" pattern="[0-9+\s\(\)\.\-]{7,}" title="Enter your WhatsApp number with country code" placeholder="WhatsApp number (with country code)" value={c.whatsapp} onChange={(e) => setC({ ...c, whatsapp: e.target.value })} data-testid="quote-whatsapp-input" />
            <button disabled={busy} className="inline-flex justify-center items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28] disabled:opacity-60" data-testid="quote-send-btn">
              <Send className="h-4 w-4" /> {busy ? "Sending..." : "Send my custom program"}
            </button>
          </form>
        )}
        <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex justify-center items-center gap-2 rounded-xl border border-[#25D366] text-[#128C7E] px-5 py-3 font-semibold hover:bg-[#25D366]/10" data-testid="quote-whatsapp-btn">
          <MessageCircle className="h-4 w-4" /> Send on WhatsApp
        </a>
      </aside>
      <aside className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-6 sm:p-8 flex flex-col gap-4" data-testid="quote-enroll-card">
        <div>
          <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">OPTION 2 · READY TO ENROLL?</p>
          <h3 className="mt-2 text-xl font-bold text-white flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#F08A3C]" /> Pay the $5,000 course tuition</h3>
          <p className="mt-1 text-sm text-[#F5ECD7]/80">Secure your spot in the AI Website Web Developer Course and finish with your own fully functional website or app, set up to generate leads. Any add-ons you picked are quoted and billed separately.</p>
        </div>
        <div className="rounded-2xl bg-white p-5"><PayPalCheckout slot="quote" addOns={picked} addOnsOther={other.trim()} bare /></div>
      </aside>
      </div>
    </div>
  );
}
